import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from "@mui/material";
import { collection, onSnapshot, orderBy, query, limit } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import moment from "moment";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import TopHeader from "../../../../Components/TopHeader/TopHeader";
import TableContainer from "../../../../Components/TableContainer/TableContainer";
import { DATABASE, FUNCTIONS } from "../../../../Config/firebase";

const statusChip = (status) => {
  const map = {
    held: { label: "In transit", color: "warning" },
    settled: { label: "Settled", color: "success" },
    cancelled: { label: "Cancelled", color: "default" },
  };
  const cfg = map[status] || { label: status, color: "default" };
  return <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />;
};

const fmtTs = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return moment(d).format("DD MMM YYYY, HH:mm");
  } catch {
    return "—";
  }
};

const CapitalRoutingMonitor = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(DATABASE, "capital_routings"), orderBy("initiated_at", "desc"), limit(100));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
        toast.error("Failed to load capital routings.");
      }
    );
    return () => unsub();
  }, []);

  const handleSettleNow = async (row) => {
    if (row.status !== "held") return;
    const confirm = await Swal.fire({
      title: "Settle routing now?",
      text: "Credits the recipient immediately (admin override of settlement window).",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Settle now",
    });
    if (!confirm.isConfirmed) return;
    try {
      const fn = httpsCallable(FUNCTIONS, "settleRoutingNow");
      const { data } = await fn({ routingId: row.id });
      toast.success(`Routing settled (${data?.status || "ok"}).`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Settle failed.");
    }
  };

  const handleCancel = async (row) => {
    if (row.status !== "held") {
      toast.info("Only routings in transit can be cancelled.");
      return;
    }
    const confirm = await Swal.fire({
      title: "Cancel routing?",
      text: "Funds will be returned to the sender.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel routing",
      confirmButtonColor: "#d32f2f",
    });
    if (!confirm.isConfirmed) return;
    try {
      const fn = httpsCallable(FUNCTIONS, "cancelRouting");
      await fn({ routingId: row.id });
      toast.success("Routing cancelled.");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Cancel failed.");
    }
  };

  const columns = [
    { field: "from_email", headerName: "From", flex: 1, minWidth: 160 },
    { field: "to_email", headerName: "To", flex: 1, minWidth: 160 },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      renderCell: (params) => `$${Number(params.row.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => statusChip(params.row.status),
    },
    {
      field: "settle_at",
      headerName: "Settles at",
      width: 170,
      renderCell: (params) => fmtTs(params.row.settle_at),
    },
    {
      field: "test_mode",
      headerName: "Test",
      width: 80,
      renderCell: (params) => (params.row.test_mode ? "Yes" : "—"),
    },
    {
      field: "actions",
      headerName: "",
      width: 220,
      renderCell: (params) =>
        params.row.status === "held" ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" color="primary" variant="outlined" onClick={() => handleSettleNow(params.row)}>
              Settle now
            </Button>
            <Button size="small" color="error" onClick={() => handleCancel(params.row)}>
              Cancel
            </Button>
          </Box>
        ) : null,
    },
  ];

  return (
    <Box>
      <TopHeader title="Capital Routing" />
      <Card sx={{ m: 2 }}>
        <CardContent>
          <Typography sx={{ fontFamily: "Poppins", fontSize: 14, color: "#6b7280", mb: 2 }}>
            Monitor internal capital routings. Funds are debited from the sender immediately and credited to the recipient after the settlement window.
          </Typography>
          <TableContainer
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            paginationShow={false}
            sx={{ minHeight: 400 }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default CapitalRoutingMonitor;
